import { z } from 'zod'

export const ExternalWebhookEventTypeSchema = z.enum([
  'sale.paid',
  'sale.refused',
  'sale.refunded',
  'sale.charged_back',
  'subscription.created',
  'subscription.canceled',
  'subscription.renewed',
  'subscription.past_due',
  'pix.generated',
  'boleto.generated',
  'checkout.abandoned',
])
export type ExternalWebhookEventType = z.infer<typeof ExternalWebhookEventTypeSchema>

export const WebhookSubscriptionStatusSchema = z.enum(['ACTIVE', 'INACTIVE', 'DELETED'])
export type WebhookSubscriptionStatus = z.infer<typeof WebhookSubscriptionStatusSchema>

export const WebhookCircuitStateSchema = z.enum(['CLOSED', 'OPEN', 'HALF_OPEN'])
export type WebhookCircuitState = z.infer<typeof WebhookCircuitStateSchema>

export const WebhookDeliveryStatusSchema = z.enum([
  'PENDING',
  'SENDING',
  'DELIVERED',
  'RETRYING',
  'DEAD',
  'SUPPRESSED',
])
export type WebhookDeliveryStatus = z.infer<typeof WebhookDeliveryStatusSchema>

export const WebhookPayloadEnvelopeSchema = z.object({
  id: z.string(),
  type: ExternalWebhookEventTypeSchema,
  apiVersion: z.string(),
  createdAt: z.string(),
  data: z.unknown(),
})
export type WebhookPayloadEnvelope = z.infer<typeof WebhookPayloadEnvelopeSchema>

export const WebhookCatalogItemSchema = z.object({
  eventType: ExternalWebhookEventTypeSchema,
  label: z.string(),
  description: z.string(),
  example: WebhookPayloadEnvelopeSchema,
})
export type WebhookCatalogItem = z.infer<typeof WebhookCatalogItemSchema>

export const WebhookCatalogSchema = z.array(WebhookCatalogItemSchema)

export const WebhookSubscriptionSchema = z.object({
  id: z.string(),
  name: z.string(),
  url: z.string(),
  eventTypes: z.array(ExternalWebhookEventTypeSchema),
  productIds: z.array(z.string()).optional(),
  status: WebhookSubscriptionStatusSchema,
  circuitState: WebhookCircuitStateSchema.catch('CLOSED'),
  consecutiveFailures: z.number().catch(0),
  circuitOpenUntil: z.string().optional(),
  failureStreakStartedAt: z.string().optional(),
  disabledAt: z.string().optional(),
  disabledReason: z.string().optional(),
  secret: z.string(),
})
export type WebhookSubscription = z.infer<typeof WebhookSubscriptionSchema>

export const WebhookSubscriptionListSchema = z.array(WebhookSubscriptionSchema)

export const CreateWebhookInputSchema = z.object({
  name: z.string().min(1),
  url: z.string().url(),
  eventTypes: z.array(ExternalWebhookEventTypeSchema).min(1),
  productIds: z.array(z.string().min(1)).optional(),
})
export type CreateWebhookInput = z.infer<typeof CreateWebhookInputSchema>

export const UpdateWebhookInputSchema = z.object({
  name: z.string().min(1).optional(),
  url: z.string().url().optional(),
  eventTypes: z.array(ExternalWebhookEventTypeSchema).min(1).optional(),
  productIds: z.array(z.string().min(1)).optional(),
  status: z.enum(['ACTIVE', 'INACTIVE']).optional(),
})
export type UpdateWebhookInput = z.infer<typeof UpdateWebhookInputSchema>

export const WebhookDeliveryListItemSchema = z.object({
  id: z.string(),
  subscriptionId: z.string(),
  sourceEventId: z.string(),
  externalEventType: ExternalWebhookEventTypeSchema,
  deliveryEventId: z.string(),
  status: WebhookDeliveryStatusSchema,
  attempts: z.number(),
  maxAttempts: z.number(),
  availableAt: z.string(),
  lastHttpStatus: z.number().optional(),
  lastError: z.string().optional(),
  isTest: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
})
export type WebhookDeliveryListItem = z.infer<typeof WebhookDeliveryListItemSchema>

export const WebhookDeliveryLogSchema = z.object({
  id: z.string(),
  deliveryId: z.string(),
  attempt: z.number(),
  httpStatus: z.number().optional(),
  requestHeaders: z.unknown(),
  responseBody: z.string().optional(),
  durationMs: z.number(),
  outcome: z.string(),
  error: z.string().optional(),
  createdAt: z.string(),
})
export type WebhookDeliveryLog = z.infer<typeof WebhookDeliveryLogSchema>

export const WebhookDeliveryDetailSchema = WebhookDeliveryListItemSchema.extend({
  payload: WebhookPayloadEnvelopeSchema,
  logs: z.array(WebhookDeliveryLogSchema),
})
export type WebhookDeliveryDetail = z.infer<typeof WebhookDeliveryDetailSchema>

export const WebhookDeliveriesPageSchema = z.object({
  items: z.array(WebhookDeliveryListItemSchema),
  nextCursor: z.string().nullable(),
  hasMore: z.boolean(),
})
export type WebhookDeliveriesPage = z.infer<typeof WebhookDeliveriesPageSchema>

export const WebhookTestResultSchema = z.object({
  deliveryId: z.string(),
  deliveryEventId: z.string(),
  sourceEventId: z.string(),
  externalEventType: ExternalWebhookEventTypeSchema,
  status: z.literal('queued'),
  isTest: z.literal(true),
})
export type WebhookTestResult = z.infer<typeof WebhookTestResultSchema>
